import { Metadata } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import HomepageHeader from '@/components/HomepageHeader';
import HomepageFooter from '@/components/HomepageFooter';

interface StudioPageProps {
  params: {
    slug: string;
  };
}

export async function generateMetadata({ params }: StudioPageProps): Promise<Metadata> {
  // In a real app, you would fetch studio data based on slug
  return {
    title: 'Studio Details - Female Fitness Finder',
    description: 'Find detailed information about women-only fitness studios and spaces',
  };
}

export default function StudioDetailPage({ params }: StudioPageProps) {
  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <HomepageHeader />
      
      <main className="pt-[140px]">
        {/* Gallery Section */}
        <section className="py-12 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Large Image - Takes 2 columns and 2 rows */}
            <div className="col-span-2 row-span-2 relative h-[400px] rounded-[20px] overflow-hidden group cursor-pointer">
              <Image
                src="/hero-banner.jpg"
                alt="Studio Main View"
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>

            {/* Top Right Image */}
            <div className="col-span-2 relative h-[192px] rounded-[20px] overflow-hidden group cursor-pointer">
              <Image
                src="/gym-card-1.jpg"
                alt="Studio Equipment"
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>

            {/* Bottom Right - 2 Small Images */}
            <div className="relative h-[192px] rounded-[20px] overflow-hidden group cursor-pointer">
              <Image
                src="/gym-card-2.jpg"
                alt="Studio Class"
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>

            <div className="relative h-[192px] rounded-[20px] overflow-hidden group cursor-pointer">
              <Image
                src="/about-fff.jpg"
                alt="Studio Member"
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {/* Show All Photos Button Overlay */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <button className="px-6 py-3 bg-white text-[#2E2A3A] rounded-[10px] font-['Archivo',sans-serif] text-[14px] font-medium flex items-center gap-2 hover:bg-[#FBEFF9] transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Show All Photos
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Studio Details Section */}
        <section className="py-20 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-8">
              {/* About Section */}
              <div className="bg-white rounded-[24px] p-8 md:p-12">
                <h2 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A] mb-6">
                  About This Studio
                </h2>
                <div className="space-y-4">
                  <p className="font-['Archivo',sans-serif] text-[18px] text-[#6e6672] leading-[30px]">
                    Empowered Gyms Bromley is more than just a fitness studio - it's a sanctuary for women to build strength, confidence, and community. Our state-of-the-art facility offers a wide range of classes tailored specifically for women's fitness needs.
                  </p>
                  <p className="font-['Archivo',sans-serif] text-[18px] text-[#6e6672] leading-[30px]">
                    Founded in 2020 by certified fitness professionals, we've created an environment where women of all fitness levels can feel comfortable, supported, and motivated to achieve their health and wellness goals.
                  </p>
                </div>
              </div>

              {/* Classes & Services */}
              <div className="bg-white rounded-[24px] p-8 md:p-12">
                <h2 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A] mb-6">
                  Classes & Services
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FBEFF9] rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.57 14.86L22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2 2.43 3.57 1 5.71 7.29 16.29 18.71 22 20.57 18.86 17 15.29z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">
                        Strength Training
                      </h3>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">
                        Build muscle and increase endurance with our women-focused strength programs.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FBEFF9] rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">
                        Yoga & Mindfulness
                      </h3>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">
                        Find balance and inner peace with our specialized yoga and meditation classes.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FBEFF9] rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">
                        Pilates
                      </h3>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">
                        Strengthen your core and improve flexibility with our expert-led Pilates sessions.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FBEFF9] rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">
                        Cardio & HIIT
                      </h3>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">
                        High-energy workouts designed to boost your metabolism and burn calories.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Amenities */}
              <div className="bg-white rounded-[24px] p-8 md:p-12">
                <h2 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A] mb-6">
                  Amenities
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">Changing Rooms</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">Showers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">Lockers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">Parking</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">WiFi</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">Nutrition Area</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Contact Card */}
              <div className="bg-white rounded-[24px] p-8">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                  Get in Touch
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <div>
                      <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Location</p>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">123 High Street, Bromley</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                    <div>
                      <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Phone</p>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">+44 20 1234 5678</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-[#8A7296]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                    <div>
                      <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Email</p>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">info@empoweredgyms.com</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <button className="w-full px-6 py-4 bg-[#8A7296] hover:bg-[#7a647d] text-white rounded-[10px] transition-colors font-['Archivo',sans-serif] text-[16px] font-medium flex items-center justify-center gap-2">
                    Send Enquiry
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                  <button className="w-full px-6 py-4 bg-white text-[#8A7296] hover:bg-[#8A7296] hover:text-white border border-[#8A7296] rounded-[10px] transition-colors font-['Archivo',sans-serif] text-[16px] font-medium flex items-center justify-center gap-2">
                    Book a Class
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="bg-white rounded-[24px] p-8">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                  Opening Hours
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">Monday - Friday</span>
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">6:00 AM - 10:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">Saturday</span>
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">7:00 AM - 8:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">Sunday</span>
                    <span className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">8:00 AM - 6:00 PM</span>
                  </div>
                </div>
              </div>

              {/* Verification Badge */}
              <div className="bg-[#FBEFF9] rounded-[24px] p-8 text-center">
                <div className="w-16 h-16 bg-[#8A7296] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">
                  Verified by FFF
                </h3>
                <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                  This studio has been independently verified and meets our standards for women-only fitness spaces.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Search */}
        <section className="py-8 bg-white">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              href="/search-process" 
              className="inline-flex items-center gap-2 text-[#8A7296] hover:text-[#7a647d] transition-colors font-['Archivo',sans-serif] text-[16px] font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Search Results
            </Link>
          </div>
        </section>
      </main>

      <HomepageFooter />
    </div>
  );
}
